import React, { useMemo } from 'react';

import {
  Card,
  Col,
  Flex,
  Form,
  Row,
  Table,
  TableProps,
  Tooltip,
} from 'antd-v5';
import { MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Heading } from 'scouting-ui/lib';
import { cssVars } from 'scouting-ui/lib/variables';

import type { MBCRecord } from '@appTypes/esb';
import type {
  PaginationRequest,
  PaginationResponse,
} from '@appTypes/esb/advancements/v2/meritBadges/counselors/search';
import { BaseMessage } from '@modules/bulkEntry/components/InfoMessages/BaseMessage';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import MbcSearchCSV from '@modules/mbc/components/MbcSearchCSV';
import type { SearchFormState } from '@modules/meritBadgeCounselors/types';
import { useGetCompletePersonProfileQuery } from '@modules/rtk/esb-api';
import { ConditionalRender, TableMobile } from '@shared/components';
import {
  hasNewPermissionSel,
  isCouncilOrgActiveSel,
  isMobileSel,
  personGuidSel,
} from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import { newPermissions } from '@shared/newPermissions';
import { usePositions } from '@user/duck/hooks';

import {
  SearchTableActions,
  TableMode,
  usersLevelCouncil,
  usersLevelDistrict,
} from '../../constants';
import { defaultSortBy, getPrimaryAddress } from '../../duck/utils';
import { MbcSearchTableColumn } from '../column';
import styles from './MbcTable.less';
import rows from './MbcTableRows';

interface MbcTableProps {
  resSearchMBC: MBCRecord[];
  onEdit?: (memberId: number) => () => void;
  onActionClick: (action: SearchTableActions, counselor: MBCRecord) => void;
  mbcSearchLoading: boolean;
  mode?: TableMode;
  pagination?: PaginationResponse;
  setPagination?: React.Dispatch<
    React.SetStateAction<{
      perPage: number;
      currentPage: number;
      totalRecords: number;
    }>
  >;
  onChangePage?: (page: number, size: number) => void;
  searchValue: SearchFormState;
  editMBsWarning?: string;
  searchMBC?: (params: { currentPagination: PaginationRequest }) => void;
}

const MbcTable: React.FC<MbcTableProps> = props => {
  const {
    resSearchMBC,
    onEdit,
    mbcSearchLoading,
    mode,
    pagination,
    setPagination,
    onChangePage,
    searchValue,
    editMBsWarning,
    searchMBC,
  } = props;
  const enableSortWithAPI = featureFlags.getFlag('SBL_6789_SORT_WITH_API');
  const isMobile = useSelector(isMobileSel);
  const isCouncilOrg = useSelector(isCouncilOrgActiveSel);
  const hasResult = resSearchMBC?.length > 0;
  const form = Form.useFormInstance<SearchFormState>();
  const searchType = searchValue.orgGuidType ?? '';

  const canEditAndAssign = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.MBC_TABLE_EDIT_ASSIGN),
  );
  const { hasPositions } = usePositions();
  const hasDistrictLevelPosition = hasPositions(usersLevelDistrict);
  const hasCouncilLevelPosition = hasPositions(usersLevelCouncil);

  const personGuid = useSelector(personGuidSel) || '';
  const { data: userProfile } = useGetCompletePersonProfileQuery(
    { personGuid: personGuid },
    { skip: !personGuid },
  );
  const userZip = getPrimaryAddress(userProfile, 'zipCode');
  const showDistance = !!(form.getFieldValue('zipCode') || userZip);

  const columns = useMemo(
    () =>
      MbcSearchTableColumn(
        resSearchMBC,
        showDistance,
        isCouncilOrg,
        canEditAndAssign,
        onEdit,
        props.onActionClick,
        mode,
        searchType,
        editMBsWarning,
      ),
    [
      resSearchMBC,
      showDistance,
      isCouncilOrg,
      canEditAndAssign,
      onEdit,
      props.onActionClick,
      mode,
      searchType,
      editMBsWarning,
    ],
  );

  const handleTableChange: TableProps<MBCRecord>['onChange'] = (
    newPagination,
    _filters,
    sorter,
    extra,
  ) => {
    if (extra.action === 'paginate' && setPagination) {
      setPagination(prev => ({
        ...prev,
        currentPage: newPagination.current!,
        perPage: newPagination.pageSize!,
        totalRecords: pagination?.totalRecords ?? 0,
      }));

      if (enableSortWithAPI) {
        if (searchMBC) {
          const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
          const sortDirection =
            currentSorter.order === 'descend' ? 'desc' : 'asc';

          searchMBC({
            currentPagination: {
              page: newPagination.current!,
              perPage: newPagination.pageSize!,
              sortBy: String(
                currentSorter?.columnKey || defaultSortBy(searchValue),
              ),
              sortOrder: sortDirection,
            },
          });
        }
      } else if (onChangePage)
        onChangePage(newPagination.current!, newPagination.pageSize!);
    }

    if (extra.action === 'filter' && setPagination) {
      setPagination(prev => ({
        ...prev,
      }));
    }

    if (enableSortWithAPI) {
      if (extra.action === 'sort' && searchMBC) {
        const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        const sortDirection =
          currentSorter.order === 'descend' ? 'desc' : 'asc';

        searchMBC({
          currentPagination: {
            page: newPagination.current!,
            perPage: newPagination.pageSize!,
            sortBy: String(
              currentSorter?.columnKey || defaultSortBy(searchValue),
            ),
            sortOrder: sortDirection,
          },
        });
      }
    }
  };
  const tableSettings = useMemo(
    () => ({
      sticky: { offsetHeader: 60 },
      pagination: pagination
        ? ({
            align: 'center',
            current: pagination.currentPage,
            pageSize: pagination.perPage,
            total: pagination.totalRecords,
          } as const)
        : undefined,
    }),
    [pagination],
  );

  return (
    <Card>
      <Flex vertical gap={10}>
        <Row justify="start" align="middle">
          <Col className={styles.tableHeader}>
            <Heading font="display-h5">
              <FormattedMessage id="mbc.mbcTable" />{' '}
              <Tooltip
                title={intl.formatMessage({
                  id: 'mbc.mbcTable.filterDisclaimer',
                })}
              >
                <MdInfo
                  className={styles.infoIcon}
                  color={cssVars['color-info']}
                />
              </Tooltip>
            </Heading>
          </Col>
          <ConditionalRender
            value={
              featureFlags.getFlag('SBL_4400_DOWNLOAD_MBC_FILE') &&
              mode !== TableMode.ASSIGN_MBCS
            }
          >
            <Col xs={24} md={12} className={styles.btnCSV}>
              <MbcSearchCSV counselors={resSearchMBC} />
            </Col>
          </ConditionalRender>
        </Row>
        <Row justify="center" align="middle">
          <Col xs={24}>
            <ConditionalRender value={!hasResult}>
              <BaseMessage
                title={<FormattedMessage id="mbc.mbcTable.noFound" />}
                description={
                  <FormattedMessage id="mbc.mbcTable.noFound.description" />
                }
              />
            </ConditionalRender>
            <ConditionalRender value={hasResult}>
              {!isMobile && (
                <Table<MBCRecord>
                  size="small"
                  sticky={tableSettings.sticky}
                  rowKey={record =>
                    `${record.memberId}-${record.counselorAvailabilityId}`
                  }
                  dataSource={resSearchMBC}
                  scroll={{ x: 'max-content' }}
                  pagination={tableSettings.pagination}
                  columns={columns}
                  loading={mbcSearchLoading}
                  onChange={handleTableChange}
                />
              )}
              {isMobile && (
                <TableMobile
                  items={resSearchMBC}
                  loading={mbcSearchLoading}
                  rows={rows({
                    hasCouncilLevelPosition,
                    hasDistrictLevelPosition,
                    hasEditAssignPerm: canEditAndAssign,
                    isCouncilOrg,
                    onAction: props.onActionClick,
                    onEdit,
                    searchValue,
                    showDistance,
                  })}
                />
              )}
            </ConditionalRender>
          </Col>
        </Row>
      </Flex>
    </Card>
  );
};

export default MbcTable;
